
import Vue from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  AUTH_LOADING,
  GET_USER_DETAILS,
  GET_PLAIN_PASSWORD,
  RESTORE_PLAIN_PASSWORD,
  UPDATE_USER_DETAILS
} from "@/store/modules/auth/constants";
import { decrypt_password } from "@/utils/crypto";
import { get_password_validator_regex } from "@/utils/global";
import {
  email,
  maxLength,
  minLength,
  not,
  required,
  requiredIf,
  sameAs
} from "vuelidate/lib/validators";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import { get_color_by_name } from "@/utils/colors";

export default Vue.extend({
  name: "Account",
  data() {
    return {
      name_icon: require("@/assets/icons/bold/user-octagon.svg"),
      email_icon: require("@/assets/icons/bold/sms.svg"),
      password_icon: require("@/assets/icons/bold/lock.svg"),
      show_password: false,
      edit_mode: false,
      edit_details_form: false,
      show_current_password: false,
      show_new_password: false,
      password: "**********",
      email_changed: false,
      avatar_uri: null,
      avatar_file: null as File | null,
      avatar_preview: null as string | null,
      show_avatar_error: false,
      avatar_error_message: "",
      update_details_form: {
        name: "",
        email: "",
        current_password: "",
        new_password: ""
      }
    };
  },
  validations() {
    return {
      update_details_form: {
        name: {
          required
        },
        email: {
          required,
          email: email
        },
        current_password: {
          required,
          min: minLength(8),
          max: maxLength(16),
          valid: (value: string) => get_password_validator_regex().test(value),
          change: (value: string) => {
            if (!value) return false;
            // If plain password is available, use it for comparison
            if (this.get_plain_password) {
              return value.trim() === this.get_plain_password.trim();
            }
            const storedPassword = this.get_user_details?.password;
            if (!storedPassword) return true; // If no stored password, skip validation

            // Check if password is bcrypt hash (starts with $2a$, $2b$, or $2y$)
            // Bcrypt hashes cannot be decrypted, so we skip frontend validation
            // The backend will validate it properly
            if (
              storedPassword.startsWith("$2a$") ||
              storedPassword.startsWith("$2b$") ||
              storedPassword.startsWith("$2y$")
            ) {
              return true; // Skip validation for bcrypt - backend will handle it
            }

            // For AES-encrypted passwords (legacy), decrypt and compare
            const decryptedPassword = decrypt_password(storedPassword);
            // Compare trimmed values to avoid whitespace issues
            return value.trim() === decryptedPassword.trim();
          }
        },
        new_password: {
          required: requiredIf((value) => value.length > 1),
          min: minLength(8),
          max: maxLength(16),
          unique: not(sameAs("current_password")),
          valid: (value) =>
            value.length > 1 ? get_password_validator_regex().test(value) : true
        }
      }
    };
  },
  async mounted() {
    this.update_details_form.name =
      this.get_user_details?.first_name +
      " " +
      this.get_user_details?.last_name;
    this.update_details_form.email = this.get_user_details.email;
    await this.restore_plain_password();
    this.$nextTick(() => {
      this.setCurrentPassword();
    });
  },
  watch: {
    edit_mode(newVal: boolean) {
      if (newVal) {
        this.show_current_password = false;
        this.show_new_password = false;
        // When entering edit mode, pre-fill current password and clear new password
        this.$nextTick(() => {
          this.setCurrentPassword();
        });
      }
    },
    get_plain_password() {
      // When plain password becomes available, update current password field
      if (this.edit_mode) {
        this.$nextTick(() => {
          this.setCurrentPassword();
        });
      }
    }
  },
  computed: {
    ...mapGetters("auth", {
      get_user_details: GET_USER_DETAILS,
      auth_loading: AUTH_LOADING,
      get_plain_password: GET_PLAIN_PASSWORD
    }),
    canRevealPassword(): boolean {
      // We can safely reveal only if we either:
      // - still have the plain password in memory, or
      // - have a legacy AES-encrypted password that we can decrypt.
      if (this.get_plain_password) return true;
      const storedPassword = this.get_user_details?.password;
      if (!storedPassword) return false;
      // If it's a bcrypt hash, we CANNOT recover the original password
      if (
        storedPassword.startsWith("$2a$") ||
        storedPassword.startsWith("$2b$") ||
        storedPassword.startsWith("$2y$")
      ) {
        return false;
      }
      return true;
    },
    displayPassword(): string {
      // Always show masked password by default
      if (!this.show_password) {
        return "**********";
      }

      // If plain password is available (from login/signup or localStorage), show it
      if (this.get_plain_password) {
        return this.get_plain_password;
      }

      // Use current password from edit form if user entered it or it was prefilled
      const formCurrent =
        this.update_details_form.current_password &&
        this.update_details_form.current_password !== "**********";
      if (formCurrent) {
        return this.update_details_form.current_password;
      }

      const storedPassword = this.get_user_details?.password;
      if (!storedPassword) {
        return "";
      }

      // If it's a bcrypt hash, we cannot decrypt it to plain text
      if (
        storedPassword.startsWith("$2a$") ||
        storedPassword.startsWith("$2b$") ||
        storedPassword.startsWith("$2y$")
      ) {
        // Keep showing masked value
        return "**********";
      }

      // For AES-encrypted passwords (legacy), decrypt and show
      const decrypted = decrypt_password(storedPassword);
      return decrypted || "**********";
    },
    nameModel: {
      get() {
        return this.$v.update_details_form.name?.$model || "";
      },
      set(value: string) {
        if (this.$v.update_details_form.name) {
          this.$v.update_details_form.name.$model = value;
        }
      }
    },
    emailModel: {
      get() {
        return this.$v.update_details_form.email?.$model || "";
      },
      set(value: string) {
        if (this.$v.update_details_form.email) {
          this.$v.update_details_form.email.$model = value;
        }
      }
    },
    currentPasswordModel: {
      get() {
        return this.$v.update_details_form.current_password?.$model || "";
      },
      set(value: string) {
        if (this.$v.update_details_form.current_password) {
          this.$v.update_details_form.current_password.$model = value;
        }
      }
    },
    newPasswordModel: {
      get() {
        return this.$v.update_details_form.new_password?.$model || "";
      },
      set(value: string) {
        if (this.$v.update_details_form.new_password) {
          this.$v.update_details_form.new_password.$model = value;
        }
      }
    }
  },
  methods: {
    ...mapMutations({
      root_notification: ROOT_NOTIFICATION
    }),
    ...mapActions("auth", {
      restore_plain_password: RESTORE_PLAIN_PASSWORD,
      update_user_details: UPDATE_USER_DETAILS
    }),
    async togglePasswordVisibility() {
      await this.restore_plain_password();
      this.show_password = !this.show_password;
    },
    setCurrentPassword() {
      // Ensure New Password is never prefilled with current password
      this.update_details_form.new_password = "";
      if (this.$v.update_details_form?.new_password) {
        this.$v.update_details_form.new_password.$model = "";
      }

      // Get the visible password from displayPassword (which handles plain password or decryption)
      // Temporarily show password to get the actual value
      const wasShowing = this.show_password;
      this.show_password = true;
      const visiblePassword = this.displayPassword;
      this.show_password = wasShowing;

      // Only set current_password (never new_password). Only set if we got a real password (not masked)
      if (
        visiblePassword &&
        visiblePassword !== "**********" &&
        visiblePassword.length > 0
      ) {
        this.update_details_form.current_password = visiblePassword;
        if (this.$v.update_details_form?.current_password) {
          this.$v.update_details_form.current_password.$model = visiblePassword;
        }
      } else if (this.get_plain_password) {
        this.update_details_form.current_password = this.get_plain_password;
        if (this.$v.update_details_form?.current_password) {
          this.$v.update_details_form.current_password.$model =
            this.get_plain_password;
        }
      }
    },
    same_current_password(value: string): boolean {
      const p = this.get_user_details?.password;
      if (!p) return false;
      return value === decrypt_password(p);
    },
    transform_edit_icon(icon: HTMLElement): HTMLElement {
      const color = get_color_by_name("primary");
      for (let node of icon.children) node.setAttribute("stroke", color);
      return icon;
    },
    trigger_file_input() {
      (this.$refs.avatar_input as HTMLInputElement).click();
    },
    handle_avatar_change(event: Event) {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files[0]) {
        const file = input.files[0];

        // Validate file type
        if (!file.type.match("image.*")) {
          this.show_avatar_error = true;
          this.avatar_error_message = this.$t(
            "shared.settings.account.avatar-image-only"
          ).toString();
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          this.show_avatar_error = true;
          this.avatar_error_message = this.$t(
            "shared.settings.account.avatar-size-error"
          ).toString();
          return;
        }

        this.avatar_file = file;
        this.show_avatar_error = false; // Clear error when avatar is selected
        this.avatar_error_message = "";

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.avatar_preview = e.target?.result as string;
        };
        reader.onerror = () => {
          this.show_avatar_error = true;
          this.avatar_error_message = this.$t(
            "shared.settings.account.avatar-preview-error"
          ).toString();
        };
        reader.readAsDataURL(file);
      }
    },
    async update_details() {
      // Check if avatar is required
      if (!this.avatar_file && !this.get_user_details.avatar_uri) {
        this.show_avatar_error = true; // Show error message
        this.avatar_error_message = this.$t(
          "shared.settings.account.avatar-required"
        ).toString();
        return;
      }

      this.$v.$touch();
      if (!this.$v.$invalid) {
        const form = new FormData();
        const name = this.update_details_form.name;
        const split_name = name.split(" ");
        const first_name = split_name[0];
        const email = this.update_details_form.email;
        const current_name =
          this.get_user_details.first_name +
          " " +
          this.get_user_details.last_name;

        if (
          name === current_name &&
          email === this.get_user_details.email &&
          !this.update_details_form.new_password &&
          !this.avatar_file
        ) {
          this.root_notification(this.$t("shared.settings.account.no-update"));
          return;
        }
        let last_name: string | null = null;
        if (split_name.length > 1) {
          last_name = split_name.slice(1).join(" ");
          form.append("last_name", last_name as never);
        } else form.append("last_name", "");
        form.append("first_name", first_name);
        if (this.update_details_form.new_password) {
          form.append("password", this.update_details_form.new_password);
        }
        if (email !== this.get_user_details.email) {
          form.append("email", email);
          this.email_changed = true;
        }
        // Append avatar file if it exists
        if (this.avatar_file) {
          form.append("avatar", this.avatar_file);
        }
        if (this.get_user_details.avatar_uri) {
          form.append("avatar_uri", this.get_user_details.avatar_uri);
        }

        const result = await this.update_user_details(form);
        if (result) {
          this.root_notification("Profile Updated Successfully");

          // For security, never keep the password fields populated after update.
          // Require the user to re-enter the current password next time.
          this.update_details_form.current_password = "";
          this.update_details_form.new_password = "";

          // Reset password field validation state if available
          if (this.$v.update_details_form?.current_password) {
            this.$v.update_details_form.current_password.$reset();
          }
          if (this.$v.update_details_form?.new_password) {
            this.$v.update_details_form.new_password.$reset();
          }

          // Hide password visibility toggles after successful update
          this.show_password = false;
          this.show_current_password = false;
          this.show_new_password = false;

          if (this.email_changed) {
            await this.$router.push({
              name: "verify-email"
            });
            this.email_changed = false;
          }
        }
      }
    }
  }
});
